import React, { useState } from 'react';
import LinkedInHeader from './LinkedInHeader';
import './LinkedInTheme.css';
import { ChevronDown } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

const PrivacyTerms = () => {
  const { t } = useTranslation();
  const [selectedSection, setSelectedSection] = useState('privacy');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const sections = [
    { id: 'privacy', label: t('privacyTerms.menu.privacy') },
    { id: 'user-agreement', label: t('privacyTerms.menu.userAgreement') },
    { id: 'gdpr', label: t('privacyTerms.menu.gdpr') },
    { id: 'kvkk', label: t('privacyTerms.menu.kvkk') }
  ];

  const activeSectionLabel = sections.find(s => s.id === selectedSection)?.label;

  const renderContent = () => {
    switch (selectedSection) {
      case 'privacy':
        return (
          <article>
            <h2 className="legal-title">{t('privacyTerms.privacy.title')}</h2>
            <p className="legal-intro">{t('privacyTerms.privacy.intro')}</p>
            
            <section className="legal-section">
              <h3>{t('privacyTerms.privacy.section1.title')}</h3>
              <p>{t('privacyTerms.privacy.section1.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.privacy.section2.title')}</h3>
              <p>{t('privacyTerms.privacy.section2.text')}</p>
              <ul>
                <li><Trans i18nKey="privacyTerms.privacy.section2.list.identity"><strong>Identity Data:</strong> includes first name, last name, username or similar identifier, title, and date of birth.</Trans></li>
                <li><Trans i18nKey="privacyTerms.privacy.section2.list.contact"><strong>Contact Data:</strong> includes email address and telephone numbers.</Trans></li>
                <li><Trans i18nKey="privacyTerms.privacy.section2.list.professional"><strong>Professional Data:</strong> includes CV/Resume content, employment history, skills, and simulation performance results (AI Twin data).</Trans></li>
                <li><Trans i18nKey="privacyTerms.privacy.section2.list.technical"><strong>Technical Data:</strong> includes internet protocol (IP) address, your login data, browser type and version, and operating system.</Trans></li>
              </ul>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.privacy.section3.title')}</h3>
              <p>{t('privacyTerms.privacy.section3.text')}</p>
              <ul>
                <li>{t('privacyTerms.privacy.section3.list.item1')}</li>
                <li>{t('privacyTerms.privacy.section3.list.item2')}</li>
                <li>{t('privacyTerms.privacy.section3.list.item3')}</li>
                <li>{t('privacyTerms.privacy.section3.list.item4')}</li>
              </ul>
            </section>

             <section className="legal-section">
              <h3>{t('privacyTerms.privacy.section4.title')}</h3>
              <p>{t('privacyTerms.privacy.section4.text')}</p>
            </section>
          </article>
        );
      case 'user-agreement':
        return (
          <article>
            <h2 className="legal-title">{t('privacyTerms.userAgreement.title')}</h2>
            <p className="legal-intro">{t('privacyTerms.userAgreement.intro')}</p>
            
            <section className="legal-section">
              <h3>{t('privacyTerms.userAgreement.section1.title')}</h3>
              <p>{t('privacyTerms.userAgreement.section1.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.userAgreement.section2.title')}</h3>
              <p>{t('privacyTerms.userAgreement.section2.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.userAgreement.section3.title')}</h3>
              <p>{t('privacyTerms.userAgreement.section3.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.userAgreement.section4.title')}</h3>
              <p>{t('privacyTerms.userAgreement.section4.text')}</p>
              <ul>
                <li>{t('privacyTerms.userAgreement.section4.list.item1')}</li>
                <li>{t('privacyTerms.userAgreement.section4.list.item2')}</li>
                <li>{t('privacyTerms.userAgreement.section4.list.item3')}</li>
                <li>{t('privacyTerms.userAgreement.section4.list.item4')}</li>
              </ul>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.userAgreement.section5.title')}</h3>
              <p>{t('privacyTerms.userAgreement.section5.text')}</p>
            </section>
          </article>
        );
      case 'gdpr':
        return (
          <article>
            <h2 className="legal-title">{t('privacyTerms.gdpr.title')}</h2>
            <p className="legal-intro">{t('privacyTerms.gdpr.intro')}</p>
            
            <section className="legal-section">
              <h3>{t('privacyTerms.gdpr.section1.title')}</h3>
              <p>{t('privacyTerms.gdpr.section1.text')}</p>
              <ul>
                <li><Trans i18nKey="privacyTerms.gdpr.section1.list.access"><strong>The right to access:</strong> You have the right to request copies of your personal data.</Trans></li>
                <li><Trans i18nKey="privacyTerms.gdpr.section1.list.rectification"><strong>The right to rectification:</strong> You have the right to request that we correct any information you believe is inaccurate.</Trans></li>
                <li><Trans i18nKey="privacyTerms.gdpr.section1.list.erasure"><strong>The right to erasure:</strong> You have the right to request that we erase your personal data ("Right to be Forgotten").</Trans></li>
                <li><Trans i18nKey="privacyTerms.gdpr.section1.list.restrict"><strong>The right to restrict processing:</strong> You have the right to request that we restrict the processing of your personal data.</Trans></li>
                <li><Trans i18nKey="privacyTerms.gdpr.section1.list.portability"><strong>The right to data portability:</strong> You have the right to request that we transfer the data that we have collected to another organization, or directly to you.</Trans></li>
              </ul>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.gdpr.section2.title')}</h3>
              <p>{t('privacyTerms.gdpr.section2.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.gdpr.section3.title')}</h3>
              <p>{t('privacyTerms.gdpr.section3.text')}</p>
            </section>
          </article>
        );
      case 'kvkk':
        return (
          <article>
            <h2 className="legal-title">{t('privacyTerms.kvkk.title')}</h2>
            <p className="legal-intro">{t('privacyTerms.kvkk.intro')}</p>
            
            <section className="legal-section">
              <h3>{t('privacyTerms.kvkk.section1.title')}</h3>
              <p>{t('privacyTerms.kvkk.section1.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.kvkk.section2.title')}</h3>
              <p>{t('privacyTerms.kvkk.section2.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.kvkk.section3.title')}</h3>
              <p>{t('privacyTerms.kvkk.section3.text')}</p>
            </section>

            <section className="legal-section">
              <h3>{t('privacyTerms.kvkk.section4.title')}</h3>
              <p>{t('privacyTerms.kvkk.section4.text')}</p>
              <ul>
                <li>{t('privacyTerms.kvkk.section4.list.item1')}</li>
                <li>{t('privacyTerms.kvkk.section4.list.item2')}</li>
                <li>{t('privacyTerms.kvkk.section4.list.item3')}</li>
                <li>{t('privacyTerms.kvkk.section4.list.item4')}</li>
              </ul>
            </section>
          </article>
        );
      default:
        return null;
    }
  };

  return (
    <div className="li-centered-container">
      
       {/* Main Card */}
       <div className="li-card" style={{ padding: '24px', minHeight: '600px' }}>
          
          {/* Navigation (Tabs) */}
          <div style={{ marginBottom: '24px', borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
             <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
               {sections.map(section => (
                 <button 
                   key={section.id}
                   onClick={() => setSelectedSection(section.id)}
                   style={{ 
                     padding: '8px 16px', 
                     borderRadius: '16px', 
                     border: selectedSection === section.id ? '1px solid #0a66c2' : '1px solid rgba(0,0,0,0.6)',
                     backgroundColor: selectedSection === section.id ? '#0a66c2' : 'transparent',
                     color: selectedSection === section.id ? '#fff' : 'rgba(0,0,0,0.6)',
                     cursor: 'pointer',
                     fontWeight: '600',
                     fontSize: '14px',
                     transition: 'all 0.2s'
                   }}
                 >
                   {section.label}
                 </button>
               ))}
             </div>
          </div>

          {/* Content */}
          <div className="legal-content">
            {renderContent()}
          </div>
       </div>

       {/* Contact Card */}
       <div className="li-card" style={{ padding: '24px', textAlign: 'center', marginTop: '16px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>{t('privacyTerms.contact.title')}</h3>
          <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '16px' }}>
            {t('privacyTerms.contact.text')}
          </p>
          <a href="mailto:dpo@gclouds.co.uk" className="li-btn-outline-primary" style={{ display: 'inline-block', width: 'auto', padding: '6px 24px' }}>
            {t('privacyTerms.contact.button')}
          </a>
       </div>
      
      {/* CSS for Internal Styling */}
      <style>{`
        .legal-title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 8px;
          color: rgba(0,0,0,0.9);
        }
        .legal-intro {
          font-size: 14px;
          color: rgba(0,0,0,0.6);
          margin-bottom: 24px;
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 16px;
        }
        .legal-section {
          margin-bottom: 24px;
        }
        .legal-section h3 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 12px;
          color: rgba(0,0,0,0.9);
        }
        .legal-section p, .legal-section li {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(0,0,0,0.75);
          margin-bottom: 8px;
        }
        .legal-section ul {
          padding-left: 20px;
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
};

export default PrivacyTerms;
